import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, DatabaseBackup, Clock, HelpCircle, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", type: "", schedule: "", how_it_works: "", where_to_check: "", verification_link: "" };

export default function Backups() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=create, {id,...}=edit
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setItems(data);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load backups");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (row) => {
    setForm({
      name: row.name || "",
      type: row.type || "",
      schedule: row.schedule || "",
      how_it_works: row.how_it_works || "",
      where_to_check: row.where_to_check || "",
      verification_link: row.verification_link || "",
    });
    setEditing(row);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onSave = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Backup name is required"); return; }
    setBusy(true);
    try {
      if (editing && editing.id) {
        await api.put(`/backups/${editing.id}`, form);
        toast.success("Backup updated");
      } else {
        await api.post("/backups", form);
        toast.success("Backup added");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!deleteRow) return;
    try {
      await api.delete(`/backups/${deleteRow.id}`);
      toast.success("Backup deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="backups-page">
      <PageHeader
        eyebrow="[10] Backups"
        title="Backup Register"
        subtitle="Document every backup: what it protects, how it runs, and where to verify it."
        actions={
          isAdmin ? (
            <Button
              data-testid="add-backup-btn"
              onClick={openCreate}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> Add Backup
            </Button>
          ) : null
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {loading && (
          <div className="font-mono text-xs text-[#7A7A7A] py-10 text-center">Loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="border border-[#E5E0D5] bg-white px-4 py-16 text-center" data-testid="backups-empty">
            <DatabaseBackup className="w-8 h-8 mx-auto text-[#C9C2B3] mb-3" strokeWidth={1.25} />
            <div className="font-mono text-xs text-[#7A7A7A] mb-3">No backups documented yet.</div>
            {isAdmin && (
              <button onClick={openCreate} data-testid="empty-add-backup-btn" className="font-mono text-xs uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]">+ Add your first backup</button>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="backups-grid">
            {items.map((row) => (
              <div
                key={row.id}
                data-testid={`backup-card-${row.id}`}
                className="border border-[#E5E0D5] bg-white flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-[#E5E0D5]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <DatabaseBackup className="w-4 h-4 text-[#F37B2A] shrink-0" strokeWidth={1.5} />
                      <h3 className="text-[#0F0F0F] font-medium tracking-tight truncate" title={row.name}>{row.name}</h3>
                    </div>
                    {row.type && (
                      <span className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A] bg-[#F0EDE3] border border-[#E5E0D5] px-2 py-0.5">
                        {row.type}
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(row)}
                        data-testid={`edit-backup-${row.id}`}
                        className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F]"
                        title="Edit"
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => setDeleteRow(row)}
                        data-testid={`delete-backup-${row.id}`}
                        className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]"
                        title="Delete"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>

                <div className="px-5 py-4 space-y-4 text-sm flex-1">
                  <Field icon={Clock} label="Schedule" value={row.schedule} testid={`backup-schedule-${row.id}`} />
                  <Field icon={HelpCircle} label="How it works" value={row.how_it_works} testid={`backup-how-${row.id}`} multiline />
                  <Field icon={MapPin} label="Where to check" value={row.where_to_check} testid={`backup-where-${row.id}`} multiline />
                  {row.verification_link && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <ExternalLink className="w-3.5 h-3.5 text-[#9A9A9A]" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">Backup Verification Link</span>
                      </div>
                      <a
                        href={/^https?:\/\//i.test(row.verification_link) ? row.verification_link : `https://${row.verification_link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`backup-link-${row.id}`}
                        className="pl-5 inline-flex items-center gap-1 text-[#F37B2A] hover:text-[#D86A1F] underline decoration-[#F37B2A]/40 hover:decoration-[#D86A1F] break-all"
                      >
                        {row.verification_link}
                        <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="font-mono text-xs text-[#9A9A9A]">Total: {items.length} {items.length === 1 ? "backup" : "backups"}</div>
        )}
      </div>

      {/* Create/edit modal */}
      <AlertDialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">
              {editing && editing.id ? "Edit backup" : "Add backup"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              Capture what this backup protects, how it runs, and where to verify it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="uppercase-label">Backup Name *</Label>
              <Input
                data-testid="form-backup-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Production File Server"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                autoFocus
              />
            </div>
            <div>
              <Label className="uppercase-label">Backup Type</Label>
              <Input
                data-testid="form-backup-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                placeholder="e.g. Veeam, Azure, Datto, Cloud"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Schedule</Label>
              <Input
                data-testid="form-backup-schedule"
                value={form.schedule}
                onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                placeholder="e.g. Daily at 2:00 AM, weekly full"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">How it works</Label>
              <Textarea
                data-testid="form-backup-how"
                value={form.how_it_works}
                onChange={(e) => setForm({ ...form, how_it_works: e.target.value })}
                rows={4}
                placeholder="Describe the backup process, retention, and what it covers…"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Where to check</Label>
              <Textarea
                data-testid="form-backup-where"
                value={form.where_to_check}
                onChange={(e) => setForm({ ...form, where_to_check: e.target.value })}
                rows={3}
                placeholder="Console URL, dashboard, or steps to verify the backup ran…"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Backup Verification Link</Label>
              <Input
                data-testid="form-backup-link"
                type="url"
                value={form.verification_link}
                onChange={(e) => setForm({ ...form, verification_link: e.target.value })}
                placeholder="https://portal.example.com/backups"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
              <p className="mt-1 font-mono text-[10px] text-[#9A9A9A]">Opens in a new tab from the backup card.</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="backup-save-btn"
              disabled={busy || !form.name.trim()}
              onClick={(e) => { e.preventDefault(); onSave(); }}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-50"
            >{busy ? "Saving…" : (editing && editing.id ? "Save changes" : "Add")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Delete this backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              &quot;{deleteRow?.name}&quot; will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-backup-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid, multiline = false }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#9A9A9A]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">{label}</span>
      </div>
      {value ? (
        <p className={`text-[#3A3A3A] pl-5 ${multiline ? "whitespace-pre-wrap" : ""}`} data-testid={testid}>{value}</p>
      ) : (
        <p className="font-mono text-xs text-[#9A9A9A] pl-5" data-testid={testid}>—</p>
      )}
    </div>
  );
}
